import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Lock } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const LockedContent = () => (
  <div className="h-60 flex flex-col items-center justify-center gap-2">
    <Lock className="size-7 text-gray-300" />
    <p className="text-sm text-gray-400 font-popinsMedium">Permission required</p>
  </div>
);

export default function DashboardTwoCharts() {
  const loginSuccess = useSelector((state) => state.auth?.loginSuccess);
  const isAssistant = loginSuccess?.data?.roleName === "assistant";
  const ap = loginSuccess?.data?.assistantPermissions;
  const can = (p) => !isAssistant || ap?.[p] === true;
  const [barData, setBarData] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);

  const [resourceData, setResourceData] = useState([]);
  const [totalResources, setTotalResources] = useState(0);
  const [isLoadingResources, setIsLoadingResources] = useState(true);

  useEffect(() => {
    fetchEventsChart();
    fetchResourceUsage();
  }, []);

  const fetchEventsChart = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) { setIsLoadingEvents(false); return; }
    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.GET_EVENTS_CHART}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (response.ok) {
        const data = Array.isArray(result?.data) ? result.data : [];
        setBarData(data.map((d) => ({ name: d.month, events: d.count })));
      }
    } catch (error) {
      console.error("Error fetching events chart:", error);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const fetchResourceUsage = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setIsLoadingResources(false);
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.GET_RESOURCE_USAGE}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (response.ok && result.statusCode === 200) {
        const data = result.data;
        setResourceData(data.resources || []);
        setTotalResources(data.total || 0);
      } else {
        console.error("Failed to fetch resource usage:", result.message);
      }
    } catch (error) {
      console.error("Error fetching resource usage:", error);
    } finally {
      setIsLoadingResources(false);
    }
  };

  // Use fetched data or empty array
  const pieData = resourceData.length > 0 ? resourceData : [];

  // Generate colors dynamically based on number of resources
  const generateColors = (count) => {
    const baseColors = [
      "#03045E",
      "#0077B6",
      "#3FC4E3",
      "#90E0EF",
      "#00B4D8",
      "#0096C7",
    ];
    if (count <= baseColors.length) {
      return baseColors.slice(0, count);
    }
    // Generate additional colors if needed
    const colors = [...baseColors];
    for (let i = baseColors.length; i < count; i++) {
      const hue = (i * 360) / count;
      colors.push(`hsl(${hue}, 70%, 50%)`);
    }
    return colors;
  };

  const pieColors = generateColors(pieData.length);

  // Calculate percentage for center display (largest resource percentage)
  const maxPercentage =
    pieData.length > 0 && totalResources > 0
      ? Math.round(
          (Math.max(...pieData.map((d) => d.value)) / totalResources) * 100,
        )
      : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
      {/* Events bar chart */}
      <div className="border border-gray-200 rounded-xl p-4 shadow-sm">
        <h2 className="text-base font-popinsSemiBold text-[#1E1B39] mb-5">Events — Past 5 Months</h2>
        {!can("events") ? <LockedContent /> : (
          <div className="h-60">
            {isLoadingEvents ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-400 font-popinsRegular">Loading…</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontFamily: "popinsRegular", fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontFamily: "popinsRegular", fontSize: 12 }} />
                  <Tooltip formatter={(val) => [val, "Events"]} />
                  <Bar dataKey="events" barSize={30} radius={[5, 5, 0, 0]}>
                    {barData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={index === barData.length - 1 ? "#3FC4E3" : "#e5e7eb"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </div>

      {/* Resource usage donut */}
      <div className="border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col justify-between">
        <h2 className="text-base font-popinsSemiBold text-[#1E1B39] mb-2">Resource Usage</h2>
        {!can("resources") ? <LockedContent /> : isLoadingResources ? (
          <div className="h-56 flex items-center justify-center">
            <p className="text-gray-400 font-popinsRegular">Loading resources...</p>
          </div>
        ) : pieData.length === 0 ? (
          <div className="h-56 flex items-center justify-center">
            <p className="text-gray-400 font-popinsRegular">No resource data available</p>
          </div>
        ) : (
          <>
            <div className="h-56 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} innerRadius={70} outerRadius={100} paddingAngle={4} dataKey="value">
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-700">{maxPercentage}%</span>
              </div>
            </div>
            <div className="flex justify-center flex-wrap gap-4 mt-3 text-sm">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: pieColors[index % pieColors.length] }} />
                  <span className="text-gray-700 font-popinsRegular">{item.name}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
